import React, { useState, useEffect } from 'react';
import api from '../api';
import { SearchIcon } from './Icons';
import CountryCombobox from './CountryCombobox';
import { isLikelyPersonalEmail } from '../utils/emailValidation';

const GUEST_TEMPLATE = {
  guestTitle: 'Mr.',
  firstName: '',
  lastName: '',
  guestEmail: '',
  personalEmail: '',
  guestPhone: '',
  profession: '',
  organization: '',
  guestDepartment: '',
  industry: '',
  address: '',
  linkedinId: '',
  country: '',
  otherDepartment: '',
  otherIndustry: '',
  generatePass: true
};

const DEPARTMENT_OPTIONS = [
  'Administration', 'Academics', 'Research', 'Student Affairs', 'Training & Placement',
  'IT Department', 'Finance', 'Human Resources', 'Operations', 'Marketing & Communications',
  'Healthcare Services', 'Legal Affairs', 'Hospitality Services', 'Media & Design',
  'Agriculture & Sustainability', 'Other'
];

const INDUSTRY_OPTIONS = [
  'Information Technology', 'Healthcare', 'Education', 'Finance & Banking', 'Legal Services',
  'Hospitality & Tourism', 'Media & Entertainment', 'Construction & Architecture', 'Manufacturing',
  'Agriculture & AgriTech', 'Research & Development', 'Government & Public Sector', 'Consulting',
  'Retail & E-commerce', 'Telecommunications', 'Other'
];

const COUNTRY_PHONE_DATA = {
  "India": { code: "+91", length: 10 },
  "United States": { code: "+1", length: 10 },
  "United Kingdom": { code: "+44", length: 10 },
  "United Arab Emirates": { code: "+971", length: 9 },
  "Singapore": { code: "+65", length: 8 },
  "Australia": { code: "+61", length: 9 },
  "Canada": { code: "+1", length: 10 },
  "Germany": { code: "+49", length: 11 },
  "France": { code: "+33", length: 9 },
  "Japan": { code: "+81", length: 10 },
  "China": { code: "+86", length: 11 },
  "Brazil": { code: "+55", length: 11 },
  "Russia": { code: "+7", length: 10 },
  "South Africa": { code: "+27", length: 9 },
  "Netherlands": { code: "+31", length: 9 },
  "Switzerland": { code: "+41", length: 9 },
  "Sweden": { code: "+46", length: 9 },
  "Norway": { code: "+47", length: 8 },
  "Denmark": { code: "+45", length: 8 },
  "Italy": { code: "+39", length: 10 },
  "Spain": { code: "+34", length: 9 },
  "Mexico": { code: "+52", length: 10 },
  "Saudi Arabia": { code: "+966", length: 9 },
  "Qatar": { code: "+974", length: 8 },
  "Oman": { code: "+968", length: 8 },
  "Kuwait": { code: "+965", length: 8 },
  "Bahrain": { code: "+973", length: 8 },
  "Afghanistan": { code: "+93", length: 9 },
  "Albania": { code: "+355", length: 9 },
  "Algeria": { code: "+213", length: 9 },
  "Argentina": { code: "+54", length: 10 },
  "Austria": { code: "+43", length: 10 },
  "Bangladesh": { code: "+880", length: 10 },
  "Belgium": { code: "+32", length: 9 },
  "Chile": { code: "+56", length: 9 },
  "Colombia": { code: "+57", length: 10 },
  "Egypt": { code: "+20", length: 10 },
  "Finland": { code: "+358", length: 9 },
  "Greece": { code: "+30", length: 10 },
  "Hong Kong": { code: "+852", length: 8 },
  "Indonesia": { code: "+62", length: 10 },
  "Iran": { code: "+98", length: 10 },
  "Ireland": { code: "+353", length: 9 },
  "Israel": { code: "+972", length: 9 },
  "Malaysia": { code: "+60", length: 9 },
  "New Zealand": { code: "+64", length: 9 },
  "Nigeria": { code: "+234", length: 10 },
  "Pakistan": { code: "+92", length: 10 },
  "Philippines": { code: "+63", length: 10 },
  "Poland": { code: "+48", length: 9 },
  "Portugal": { code: "+351", length: 9 },
  "South Korea": { code: "+82", length: 10 },
  "Sri Lanka": { code: "+94", length: 9 },
  "Thailand": { code: "+66", length: 9 },
  "Turkey": { code: "+90", length: 10 },
  "Vietnam": { code: "+84", length: 9 },
};

const formatEventDate = (start, end, isMulti) => {
  if (!start) return '—';
  const s = new Date(start);
  if (!isMulti || !end) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} - ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export default function AddGuestModal({ onClose, onSaved, faculty }) {
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventSearch, setEventSearch] = useState('');
  const [guests, setGuests] = useState([{ ...GUEST_TEMPLATE }]);

  useEffect(() => {
    const isAdmin = ['iqac', 'super_admin', 'ao'].includes(faculty?.role);
    const url = isAdmin ? '/events/all' : '/events';
    api.get(url).then(res => {
      const all = res.data.events || [];
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const filtered = all.filter(e => {
        const start = new Date(e.date);
        const end = e.isMultiDay && e.endDate ? new Date(e.endDate) : start;
        const ed = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        return ed >= now;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(filtered);
    }).catch(err => console.error('Error fetching events:', err));
  }, [faculty]);

  const updateGuest = (idx, key, val) => {
    const newGuests = [...guests];
    newGuests[idx][key] = val;
    setGuests(newGuests);
  };

  const handleDone = async () => {
    if (!selectedEvent) return;
    const g = guests[0];
    const hasBasic = g.firstName.trim() && g.guestEmail.trim() && g.guestPhone.trim() && g.country.trim() &&
        g.profession.trim() && g.organization.trim() && g.guestDepartment.trim() &&
        g.industry.trim() && g.address.trim() && g.linkedinId.trim();

    if (!hasBasic) {
      alert('Please fill all mandatory fields (marked with *)');
      return;
    }

    setLoadingModal(true);
    try {
      const g = guests[0];
      const payload = {
        ...g,
        guestName: `${g.guestTitle} ${g.firstName} ${g.lastName}`.trim(),
        guestDepartment: g.guestDepartment === 'Other' ? g.otherDepartment : g.guestDepartment,
        industry: g.industry === 'Other' ? g.otherIndustry : g.industry,
        generatePass: selectedEvent.eventMode === 'online' ? false : (selectedEvent.eventMode === 'hybrid' ? g.generatePass : true)
      };

      await api.post('/passes/bulk', {
        eventId: selectedEvent._id,
        sendEmail: true,
        guests: [payload]
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add guest');
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '640px', width: '95%' }}>
        <div className="modal-header">
          <div className="modal-title">Add Guest to Event</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '2rem' }}>
          {step === 1 ? (
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', marginBottom: '1rem' }}>Step 1: Select Event</div>

              <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', display: 'flex', pointerEvents: 'none' }}>
                  <SearchIcon style={{ width: '1rem', height: '1rem' }} />
                </span>
                <input
                  type="text"
                  placeholder="Search events by title, venue or host..."
                  value={eventSearch}
                  onChange={e => setEventSearch(e.target.value)}
                  autoComplete="off"
                  style={{
                    width: '100%',
                    height: '3.25rem',
                    padding: '0 1rem 0 2.75rem',
                    borderRadius: '14px',
                    border: '2px solid #f1f5f9',
                    background: '#f8fafc',
                    fontSize: '0.875rem',
                    color: 'var(--text-1)',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--navy)'}
                  onBlur={e => e.target.style.borderColor = '#f1f5f9'}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {events.filter(e => {
                  const s = eventSearch.toLowerCase();
                  return e.title.toLowerCase().includes(s) ||
                    e.venue?.toLowerCase().includes(s) ||
                    (e.faculty?.name || '').toLowerCase().includes(s);
                }).length === 0 ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-3)', background: '#f8fafc', borderRadius: '16px' }}>
                    {eventSearch ? 'No matching events found' : 'No upcoming events found'}
                  </div>
                ) : events.filter(e => {
                  const s = eventSearch.toLowerCase();
                  return e.title.toLowerCase().includes(s) ||
                    e.venue?.toLowerCase().includes(s) ||
                    (e.faculty?.name || '').toLowerCase().includes(s);
                }).map(e => (
                  <div
                    key={e._id}
                    className="event-preview-card"
                    onClick={() => { setSelectedEvent(e); setStep(2); }}
                    style={{
                      padding: '1rem',
                      border: selectedEvent?._id === e._id ? '2px solid var(--navy)' : '1px solid var(--border-1)',
                      background: selectedEvent?._id === e._id ? 'rgba(0,95,167,0.04)' : 'white',
                      cursor: 'pointer',
                      borderRadius: '16px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1.25rem'
                    }}
                  >
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '14px',
                      background: selectedEvent?._id === e._id ? 'var(--navy)' : '#eff6ff',
                      color: selectedEvent?._id === e._id ? 'white' : '#005fa7',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.02)'
                    }}>
                      <div style={{ fontSize: e.isMultiDay ? '14px' : '20px', fontWeight: 800, lineHeight: 1 }}>
                        {e.isMultiDay ? `${new Date(e.date).getDate()}-${new Date(e.endDate).getDate()}` : new Date(e.date).getDate()}
                      </div>
                      <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', marginTop: '2px' }}>{new Date(e.date).toLocaleDateString('en-US', { month: 'short' })}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, color: selectedEvent?._id === e._id ? 'var(--navy)' : 'var(--text-1)', fontSize: '1.0625rem' }}>{e.title}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', fontSize: '0.8125rem', color: 'var(--text-3)', alignItems: 'center' }}>
                        <span style={{ color: 'var(--navy)', fontWeight: 700 }}>Host: {e.faculty?.name || 'Faculty'}</span>
                        <span className="dot">·</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.venue}</span>
                        <span className="dot">·</span>
                        <span style={{ textTransform: 'capitalize' }}>{e.eventMode}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', marginBottom: '1.25rem' }}>Step 2: Guest Details</div>

              <div style={{ marginBottom: '2rem', padding: '1.25rem', background: 'var(--navy)', borderRadius: '16px', color: 'white', boxShadow: '0 8px 16px rgba(0,95,167,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 800, opacity: 0.8 }}>Target Event</div>
                  <div style={{ fontWeight: 800, fontSize: '1.1rem', marginTop: '2px' }}>{selectedEvent.title}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 800, opacity: 0.8 }}>Event Date</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginTop: '2px' }}>{formatEventDate(selectedEvent.date, selectedEvent.endDate, selectedEvent.isMultiDay)}</div>
                </div>
              </div>

              {guests.map((g, i) => {
                const phoneDigits = (g.guestPhone || '').replace(/\D/g, '');
                const countryData = COUNTRY_PHONE_DATA[g.country] || { code: '', length: 10 };
                const prefixDigits = countryData.code.replace(/\D/g, '');
                const actualDigits = (prefixDigits && phoneDigits.startsWith(prefixDigits)) 
                  ? phoneDigits.slice(prefixDigits.length) 
                  : phoneDigits;
                const isPhoneShort = actualDigits.length > 0 && actualDigits.length < countryData.length;
                const isWorkPersonal = isLikelyPersonalEmail(g.guestEmail);
                const isSameEmail = g.guestEmail && g.personalEmail && g.guestEmail.trim().toLowerCase() === g.personalEmail.trim().toLowerCase();
                const hasError = isWorkPersonal || isSameEmail || isPhoneShort;

                return (
                  <div key={i} style={{ 
                    marginBottom: '2rem', 
                    padding: '1.75rem', 
                    border: '1px solid var(--border-1)', 
                    borderRadius: '24px', 
                    background: 'white', 
                    position: 'relative', 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{ 
                      position: 'absolute', 
                      top: '-10px', 
                      left: '20px', 
                      background: hasError ? '#ef4444' : 'var(--navy)', 
                      padding: '2px 12px', 
                      fontSize: '0.7rem', 
                      fontWeight: 800, 
                      color: 'white', 
                      textTransform: 'uppercase', 
                      borderRadius: '20px' 
                    }}>
                      Guest Details
                    </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Title</label>
                      <select
                        value={g.guestTitle}
                        onChange={e => updateGuest(i, 'guestTitle', e.target.value)}
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 0.5rem', fontWeight: 600 }}
                      >
                        {['Mr.', 'Ms.', 'Dr.', 'Prof.', 'Mrs.'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>First Name *</label>
                      <input
                        value={g.firstName}
                        onChange={e => updateGuest(i, 'firstName', e.target.value)}
                        placeholder="e.g. John"
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                      />
                    </div>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Last Name</label>
                      <input
                        value={g.lastName}
                        onChange={e => updateGuest(i, 'lastName', e.target.value)}
                        placeholder="e.g. Doe"
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Work Email *</label>
                      <input
                        type="email"
                        value={g.guestEmail}
                        onChange={e => updateGuest(i, 'guestEmail', e.target.value)}
                        placeholder="john@company.com"
                        style={{ 
                          height: '3.25rem', 
                          borderRadius: '12px', 
                          border: (isWorkPersonal || isSameEmail) ? '2px solid #ef4444' : '2px solid #f1f5f9', 
                          background: (isWorkPersonal || isSameEmail) ? '#fff5f5' : '#f8fafc', 
                          width: '100%', 
                          padding: '0 1rem' 
                        }}
                      />
                      {isWorkPersonal && <div style={{ color: '#ef4444', fontSize: '0.65rem', fontWeight: 600, marginTop: '0.35rem' }}>Please use a work/organization email</div>}
                      {isSameEmail && !isWorkPersonal && <div style={{ color: '#ef4444', fontSize: '0.65rem', fontWeight: 600, marginTop: '0.35rem' }}>Work and personal emails must be different</div>}
                    </div>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Personal Email</label>
                      <input
                        type="email"
                        value={g.personalEmail}
                        onChange={e => updateGuest(i, 'personalEmail', e.target.value)}
                        placeholder="john.doe@gmail.com"
                        style={{ 
                          height: '3.25rem', 
                          borderRadius: '12px', 
                          border: isSameEmail ? '2px solid #ef4444' : '2px solid #f1f5f9', 
                          background: isSameEmail ? '#fff5f5' : '#f8fafc', 
                          width: '100%', 
                          padding: '0 1rem' 
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Country *</label>
                      <CountryCombobox
                        value={g.country}
                        onChange={val => {
                          const oldCountry = g.country;
                          const oldData = COUNTRY_PHONE_DATA[oldCountry];
                          const newData = COUNTRY_PHONE_DATA[val];
                          
                          updateGuest(i, 'country', val);
                          
                          if (newData) {
                            let currentPhone = g.guestPhone || '';
                            let newPhone = newData.code + ' ';
                            
                            if (oldData && currentPhone.startsWith(oldData.code)) {
                              const digitsAfter = currentPhone.slice(oldData.code.length).trim();
                              newPhone = newData.code + ' ' + digitsAfter;
                            } else if (!currentPhone.trim() || currentPhone.trim() === '+' ) {
                              newPhone = newData.code + ' ';
                            } else {
                              const hasAnyPrefix = currentPhone.startsWith('+');
                              if (hasAnyPrefix) {
                                const foundOld = Object.values(COUNTRY_PHONE_DATA).find(d => currentPhone.startsWith(d.code));
                                if (foundOld) {
                                  newPhone = newData.code + ' ' + currentPhone.slice(foundOld.code.length).trim();
                                }
                              }
                            }
                            updateGuest(i, 'guestPhone', newPhone);
                          }
                        }}
                      />
                    </div>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Mobile Number *</label>
                      <input
                        value={g.guestPhone}
                        onChange={e => {
                          const val = e.target.value;
                          const countryData = COUNTRY_PHONE_DATA[g.country];
                          if (countryData) {
                            const prefix = countryData.code + ' ';
                            if (!val.startsWith(prefix)) {
                              updateGuest(i, 'guestPhone', prefix);
                              return;
                            }
                            const digitsOnly = val.slice(prefix.length).replace(/\D/g, '');
                            if (digitsOnly.length <= countryData.length) {
                              updateGuest(i, 'guestPhone', prefix + digitsOnly);
                            }
                          } else {
                            updateGuest(i, 'guestPhone', val);
                          }
                        }}
                        placeholder="10-digit number"
                        style={{ 
                          height: '3.25rem', 
                          borderRadius: '12px', 
                          border: isPhoneShort ? '2px solid #ef4444' : '2px solid #f1f5f9', 
                          background: isPhoneShort ? '#fff5f5' : '#f8fafc', 
                          width: '100%', 
                          padding: '0 1rem' 
                        }}
                      />
                      {isPhoneShort && <div style={{ color: '#ef4444', fontSize: '0.65rem', fontWeight: 600, marginTop: '0.35rem' }}>Minimum {countryData.length} digits required</div>}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Profession *</label>
                      <input
                        value={g.profession}
                        onChange={e => updateGuest(i, 'profession', e.target.value)}
                        placeholder="e.g. Software Architect"
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                      />
                    </div>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Organization *</label>
                      <input
                        value={g.organization}
                        onChange={e => updateGuest(i, 'organization', e.target.value)}
                        placeholder="Company / University"
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: g.guestDepartment === 'Other' || g.industry === 'Other' ? '1fr' : '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Department *</label>
                      <select
                        value={g.guestDepartment}
                        onChange={e => updateGuest(i, 'guestDepartment', e.target.value)}
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                      >
                        <option value="">Select Department</option>
                        {DEPARTMENT_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      {g.guestDepartment === 'Other' && (
                        <input
                          value={g.otherDepartment}
                          onChange={e => updateGuest(i, 'otherDepartment', e.target.value)}
                          placeholder="Please specify department"
                          style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem', marginTop: '0.75rem' }}
                        />
                      )}
                    </div>
                    <div className="field">
                      <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Industry *</label>
                      <select
                        value={g.industry}
                        onChange={e => updateGuest(i, 'industry', e.target.value)}
                        style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                      >
                        <option value="">Select Industry</option>
                        {INDUSTRY_OPTIONS.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                      </select>
                      {g.industry === 'Other' && (
                        <input
                          value={g.otherIndustry}
                          onChange={e => updateGuest(i, 'otherIndustry', e.target.value)}
                          placeholder="Please specify industry"
                          style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem', marginTop: '0.75rem' }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="field" style={{ marginBottom: '1.25rem' }}>
                    <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Office Address *</label>
                    <textarea
                      value={g.address}
                      onChange={e => updateGuest(i, 'address', e.target.value)}
                      placeholder="Street, City, State, ZIP"
                      style={{ minHeight: '4.5rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0.75rem 1rem', fontFamily: 'inherit' }}
                    />
                  </div>

                  <div className="field">
                    <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>LinkedIn / Wikipedia URL *</label>
                    <input
                      value={g.linkedinId}
                      onChange={e => updateGuest(i, 'linkedinId', e.target.value)}
                      placeholder="linkedin.com/... or wikipedia.org/..."
                      style={{ height: '3.25rem', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#f8fafc', width: '100%', padding: '0 1rem' }}
                    />
                  </div>

                  {selectedEvent.eventMode === 'hybrid' && (
                    <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#f0fdf4', padding: '0.875rem 1.25rem', borderRadius: '16px', border: '1px solid #dcfce7' }}>
                      <input
                        type="checkbox"
                        id={`genPass-${i}`}
                        checked={g.generatePass}
                        onChange={e => updateGuest(i, 'generatePass', e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label htmlFor={`genPass-${i}`} style={{ fontSize: '0.875rem', fontWeight: 700, color: '#166534', cursor: 'pointer' }}>
                        Issue entry pass for this guest?
                      </label>
                    </div>
                  )}
                </div>
                );})}

              {selectedEvent.eventMode === 'hybrid' && (
                <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '14px', fontSize: '0.875rem', color: '#64748b', textAlign: 'center', fontWeight: 600, border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                  ℹ️ This is a hybrid event. You can choose to issue entry passes for each guest individually above.
                </div>
              )}
              {selectedEvent.eventMode === 'online' && (
                <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '14px', fontSize: '0.875rem', color: '#1e40af', textAlign: 'center', fontWeight: 600, border: '1px solid #dbeafe' }}>
                  💡 This is an online event. Guests will be added but no entry passes will be issued.
                </div>
              )}
              {selectedEvent.eventMode === 'offline' && (
                <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '14px', fontSize: '0.875rem', color: '#64748b', textAlign: 'center', fontWeight: 600, border: '1px solid #e2e8f0' }}>
                  ✅ This is an offline event. Entry passes will be automatically issued.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ padding: '1.5rem 2rem', background: '#f8fafc' }}>
          {step === 1 ? (
            <button className="btn btn-navy" style={{ width: '100%', height: '3.5rem', borderRadius: '16px', fontSize: '1rem', fontWeight: 800 }} disabled={!selectedEvent} onClick={() => setStep(2)}>Next Step</button>
          ) : (
            <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(1)}>Back</button>
              <button className="btn btn-navy" style={{ flex: 2 }} onClick={handleDone} disabled={loadingModal}>
                {loadingModal ? <span className="spinner" /> : 'Done'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
